import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Button, Field, Input, Textarea, Card, StatusPill, Select, Modal, Drawer, Toggle } from "@/components/kit";
import ConfirmDelete from "@/components/ConfirmDelete";
import { fmtDate } from "@/lib/dates";
import FundCardPage from "@/pages/donations/FundCardPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const initials = (n) => (n || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const STATUS_PILL = {
  draft: { tone: "gray", label: "Draft" },
  live: { tone: "green", label: "Open" },
  closed: { tone: "maroon", label: "Closed" },
};
const METHOD_LABEL = { square: "Square", cash: "Cash", check: "Check" };

function FundCard({ c, onOpen }) {
  const pct = c.goal_cents ? Math.min(100, Math.round((c.raised_cents / c.goal_cents) * 100)) : 100;
  const isFund = c.type === "fund";
  const pill = c.is_default ? { tone: "olive", label: "Always on" } : (STATUS_PILL[c.status] || STATUS_PILL.draft);
  return (
    <button data-testid={`fund-card-${c.campaign_id}`} onClick={() => onOpen(c)}
      className="text-left bg-cream border border-ink/15 rounded-xl p-4 hover:border-maroon/40 transition-colors">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span className="font-semibold text-[15px] leading-snug">{c.title}</span>
        <StatusPill tone={pill.tone} className="shrink-0">{pill.label}</StatusPill>
      </div>
      <div className="font-zilla text-3xl font-semibold text-dark-olive">{money(c.raised_cents)}</div>
      <div className="text-[12px] text-ink/55 mb-2">
        {isFund ? "Wherever it's needed most" : `of ${money(c.goal_cents)} goal`} · {c.gift_count} gift{c.gift_count === 1 ? "" : "s"}
      </div>
      <div className="h-2 rounded-full bg-ink/10 overflow-hidden">
        <div className={`h-full rounded-full ${isFund ? "bg-dark-olive" : "bg-maroon"}`} style={{ width: `${pct}%` }} />
      </div>
    </button>
  );
}

function AddFundCard({ onClick }) {
  return (
    <button data-testid="add-fund-card" onClick={onClick}
      className="text-center bg-warm-cream/40 border-2 border-dashed border-ink/20 rounded-xl p-4 hover:border-maroon/40 transition-colors flex flex-col items-center justify-center">
      <div className="font-semibold text-[15px] mb-1">Add a fund</div>
      <p className="text-[12px] text-ink/55 mb-3">Create a Goal campaign or an ongoing Fund members can give to.</p>
      <span className="inline-flex items-center gap-1.5 bg-dark-olive text-cream rounded-lg px-3 py-1.5 text-[13px] font-semibold"><Icon name="Plus" className="w-4 h-4" /> New fund</span>
    </button>
  );
}

const emptyCampaign = { type: "goal", title: "", story: "", cover_image: "", suggested_dollars: "", status: "draft", slug: "", goal_dollars: "", end_date: "", show_progress: true, show_donor_names: false };

function CampaignDrawer({ open, campaign, canEdit, onClose, onSaved, onDeleted }) {
  const [f, setF] = useState(emptyCampaign);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const isNew = !campaign?.campaign_id;
  useEffect(() => {
    if (!open) return;
    if (campaign?.campaign_id) {
      setF({
        type: campaign.type, title: campaign.title, story: campaign.story || "", cover_image: campaign.cover_image || "",
        suggested_dollars: (campaign.suggested_amounts || []).map((a) => a / 100).join(", "),
        status: campaign.status, slug: campaign.slug || "",
        goal_dollars: campaign.goal_cents ? campaign.goal_cents / 100 : "",
        end_date: campaign.end_date || "", show_progress: !!campaign.show_progress, show_donor_names: !!campaign.show_donor_names,
      });
    } else {
      setF({ ...emptyCampaign });
    }
  }, [open, campaign]);

  const upd = (k, v) => setF((p) => {
    const next = { ...p, [k]: v };
    if (k === "type" && v === "fund") next.show_progress = p.show_progress; // keep user's choice
    return next;
  });
  const switchType = (v) => setF((p) => ({ ...p, type: v, show_progress: v === "goal" }));

  const save = async () => {
    if (!f.title.trim()) return toast.error("A title is required.");
    setBusy(true);
    const body = {
      type: f.type, title: f.title, story: f.story, cover_image: f.cover_image,
      suggested_amounts: String(f.suggested_dollars || "").split(",").map((s) => Math.round(parseFloat(s) * 100)).filter((n) => n > 0),
      status: f.status, slug: f.slug,
      goal_cents: f.type === "goal" && f.goal_dollars ? Math.round(parseFloat(f.goal_dollars) * 100) : null,
      end_date: f.type === "goal" ? (f.end_date || null) : null,
      show_progress: !!f.show_progress, show_donor_names: !!f.show_donor_names,
    };
    try {
      const r = isNew ? await api.post("/campaigns", body) : await api.put(`/campaigns/${campaign.campaign_id}`, body);
      toast.success(isNew ? "Fund created" : "Saved");
      onSaved(r.data, isNew);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  const del = async () => {
    try { await api.delete(`/campaigns/${campaign.campaign_id}`); toast.success("Moved to Trash"); onDeleted(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const readOnly = !canEdit;
  return (
    <Drawer open={open} onClose={onClose} eyebrow={isNew ? "New" : (f.type === "goal" ? "Goal campaign" : "Fund")}
      title={isNew ? "Add a fund" : f.title} data-testid="campaign-drawer"
      footer={canEdit && <>
        {!isNew && !campaign?.is_default && <Button variant="danger" data-testid="campaign-delete" onClick={() => setConfirmDel(true)} className="mr-auto">Delete</Button>}
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button data-testid="campaign-save" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
      </>}>
      <ConfirmDelete open={confirmDel} onClose={() => setConfirmDel(false)} onConfirm={del} title="Delete this fund?" message="This moves it to Trash, where it can be restored for 30 days. Its gifts are kept." />
      <div className="space-y-4">
        {!campaign?.is_default && (
          <Field label="Type">
            <div className="flex gap-2">
              {[["goal", "Goal campaign"], ["fund", "Ongoing fund"]].map(([k, lbl]) => (
                <button key={k} data-testid={`campaign-type-${k}`} disabled={readOnly} onClick={() => switchType(k)}
                  className={`flex-1 px-3 py-2 rounded-lg text-[13px] font-semibold border ${f.type === k ? "bg-ink text-cream border-ink" : "border-ink/20 text-ink/60"}`}>{lbl}</button>
              ))}
            </div>
          </Field>
        )}
        <Field label="Title"><Input data-testid="campaign-title" disabled={readOnly} value={f.title} onChange={(e) => upd("title", e.target.value)} /></Field>
        <Field label="Story" hint="Shown on the public giving page."><Textarea data-testid="campaign-story" rows={4} disabled={readOnly} value={f.story} onChange={(e) => upd("story", e.target.value)} /></Field>
        <Field label="Cover image URL" hint="Optional"><Input data-testid="campaign-cover" disabled={readOnly} value={f.cover_image} onChange={(e) => upd("cover_image", e.target.value)} placeholder="https://…" /></Field>
        {f.type === "goal" && !campaign?.is_default && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Goal amount ($)"><Input type="number" data-testid="campaign-goal" disabled={readOnly} value={f.goal_dollars} onChange={(e) => upd("goal_dollars", e.target.value)} /></Field>
            <Field label="End date"><Input type="date" data-testid="campaign-end" disabled={readOnly} value={f.end_date || ""} onChange={(e) => upd("end_date", e.target.value)} /></Field>
          </div>
        )}
        <Field label="Suggested gift amounts ($)" hint="Comma separated, e.g. 25, 50, 100"><Input data-testid="campaign-suggested" disabled={readOnly} value={f.suggested_dollars} onChange={(e) => upd("suggested_dollars", e.target.value)} /></Field>
        <Field label="Status">
          <Select data-testid="campaign-status" disabled={readOnly} value={f.status} onChange={(e) => upd("status", e.target.value)}>
            <option value="draft">Draft</option>
            <option value="live">Live (reachable publicly)</option>
            <option value="closed">Closed</option>
          </Select>
        </Field>
        <Field label="Public link name" hint="Only lowercase letters, numbers and dashes."><Input data-testid="campaign-slug" disabled={readOnly} value={f.slug} onChange={(e) => upd("slug", e.target.value)} placeholder="e.g. corridor-initiative" /></Field>

        <div className="bg-warm-cream border border-ink/15 rounded-xl p-4 space-y-3">
          <div className="text-[13px] font-semibold text-ink/70">What the public sees <span className="font-normal text-ink/50">— these only change the public page, never your admin totals.</span></div>
          <div className="flex items-center justify-between">
            <span className="text-[14px]">Show progress publicly <span className="text-ink/50 text-[12px]">(total, bar, gift count)</span></span>
            <Toggle data-testid="campaign-show-progress" checked={f.show_progress} disabled={readOnly} onChange={(v) => upd("show_progress", v)} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[14px]">Show recent donor names publicly</span>
            <Toggle data-testid="campaign-show-names" checked={f.show_donor_names} disabled={readOnly} onChange={(v) => upd("show_donor_names", v)} />
          </div>
        </div>
      </div>
    </Drawer>
  );
}

export function OfflineGiftModal({ open, campaigns, onClose, onSaved }) {
  const [f, setF] = useState({ campaign_id: "", donor_name: "", email: "", amount: "", method: "cash", date: "", note: "", anonymous: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ campaign_id: campaigns.find((c) => c.is_default)?.campaign_id || campaigns[0]?.campaign_id || "", donor_name: "", email: "", amount: "", method: "cash", date: "", note: "", anonymous: false }); }, [open, campaigns]);
  const save = async () => {
    const cents = Math.round(parseFloat(f.amount) * 100);
    if (!cents || cents <= 0) return toast.error("Enter an amount.");
    setBusy(true);
    try {
      await api.post("/donations/offline", { campaign_id: f.campaign_id || null, donor_name: f.donor_name, email: f.email, amount_cents: cents, method: f.method, date: f.date || null, note: f.note, anonymous: f.anonymous });
      toast.success("Gift logged");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  return (
    <Modal open={open} onClose={onClose} title="Log an offline gift" data-testid="offline-gift-modal"
      footer={<Button data-testid="offline-save" onClick={save} disabled={busy}>{busy ? "Saving…" : "Log gift"}</Button>}>
      <div className="space-y-3">
        <Field label="Fund / campaign">
          <Select data-testid="offline-campaign" value={f.campaign_id} onChange={(e) => setF({ ...f, campaign_id: e.target.value })}>
            {campaigns.map((c) => <option key={c.campaign_id} value={c.campaign_id}>{c.title}</option>)}
          </Select>
        </Field>
        <Field label="Donor name"><Input data-testid="offline-name" value={f.donor_name} onChange={(e) => setF({ ...f, donor_name: e.target.value })} placeholder="e.g. Harold Simms" /></Field>
        <Field label="Email" hint="We match this to an existing member/contact, or add a new contact."><Input data-testid="offline-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount ($)"><Input type="number" data-testid="offline-amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
          <Field label="Method">
            <Select data-testid="offline-method" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}>
              <option value="cash">Cash</option>
              <option value="check">Check</option>
            </Select>
          </Field>
        </div>
        <Field label="Gift date" hint="Defaults to today"><Input type="date" data-testid="offline-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="Note / in honor of"><Input data-testid="offline-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
        <label className="flex items-center gap-2 text-[14px]"><input type="checkbox" data-testid="offline-anon" checked={f.anonymous} onChange={(e) => setF({ ...f, anonymous: e.target.checked })} /> List this gift as anonymous</label>
      </div>
    </Modal>
  );
}

export default function Donations({ me }) {
  const canEdit = me?.user?.is_super_admin || ["edit", "full"].includes(me?.permissions?.donations);
  const isSuper = me?.user?.is_super_admin;
  const [campaigns, setCampaigns] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [drawer, setDrawer] = useState(null);   // campaign object or {} for new
  const [offline, setOffline] = useState(false);
  const [view, setView] = useState(null);       // selected campaign_id -> fund card page
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    const [c, d] = await Promise.all([api.get("/campaigns"), api.get("/donations", { params: { limit: 25 } })]);
    setCampaigns(c.data.items); setGifts(d.data.items); setLoaded(true);
  }, []);
  useEffect(() => { load(); }, [load]);

  const makeTestGift = async () => {
    try { await api.post("/donations/test-gift", { amount_cents: 2500 }); toast.success("Simulated test gift added"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create test gift"); }
  };

  if (view) {
    return (
      <>
        <FundCardPage campaignId={view} canEdit={canEdit} isSuper={isSuper} reloadKey={reloadKey}
          onBack={() => { setView(null); load(); }} onEdit={(c) => setDrawer(c)} />
        <CampaignDrawer open={!!drawer} campaign={drawer} canEdit={canEdit}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); setReloadKey((k) => k + 1); }}
          onDeleted={() => { setDrawer(null); setView(null); load(); }} />
      </>
    );
  }

  return (
    <div data-testid="donations-page">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-1">
        <div>
          <h1 className="font-zilla text-3xl sm:text-4xl font-semibold">Donations</h1>
          <p className="text-[15px] text-ink/70">Separate from dues. Members choose where their gift goes, and that designation follows the money into your reports.</p>
        </div>
        <div className="flex gap-2">
          <a data-testid="donations-export" href={`${API}/donations/export`} className={`border border-ink/25 rounded-lg px-4 py-2.5 min-h-[44px] text-[15px] font-semibold inline-flex items-center gap-1.5 ${canEdit ? "" : "opacity-50 pointer-events-none"}`}><Icon name="Download" className="w-4 h-4" /> Export to Excel</a>
          {canEdit && <Button data-testid="log-offline-gift" onClick={() => setOffline(true)}><Icon name="Plus" className="w-4 h-4" /> Log an offline gift</Button>}
        </div>
      </div>

      {/* Fund cards */}
      <div className="grid gap-3 mt-4 mb-6" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 240px), 1fr))" }} data-testid="fund-cards">
        {campaigns.map((c) => <FundCard key={c.campaign_id} c={c} onOpen={(cp) => setView(cp.campaign_id)} />)}
        {canEdit && <AddFundCard onClick={() => setDrawer({})} />}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px] items-start">
        {/* Recent gifts */}
        <Card data-testid="recent-gifts">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-zilla text-xl font-semibold">Recent gifts</h2>
          </div>
          <p className="text-[13px] text-ink/55 mb-3">Receipts send automatically on every paid gift.</p>
          {loaded && gifts.length === 0 && <div className="text-center text-ink/50 py-10 text-[14px]">No gifts yet.</div>}
          <div>
            {gifts.map((g) => (
              <div key={g.donation_id} data-testid={`gift-row-${g.donation_id}`} className="flex items-center gap-3 py-3 border-b border-ink/8 last:border-0">
                <div className="w-9 h-9 rounded-full bg-ink/10 flex items-center justify-center text-[12px] font-bold text-ink/60 shrink-0">{g.anonymous ? "–" : initials(g.donor_name)}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold truncate">{g.anonymous ? "Anonymous" : (g.donor_name || "—")}</div>
                  <div className="text-[12px] text-ink/55">{fmtDate(g.date)} · {METHOD_LABEL[g.method] || g.method}{g.billing_interval === "monthly" ? " · monthly" : ""}{g.simulated && isSuper ? " · SIMULATED" : ""}</div>
                </div>
                <div className="text-[13px] text-dark-olive font-semibold hidden sm:block truncate max-w-[160px]">{g.campaign_title}</div>
                <div className="flex items-center gap-2 shrink-0">
                  {g.status !== "paid" && <StatusPill tone={g.status === "simulated" ? "gold" : (g.status === "failed" ? "maroon" : "gray")}>{g.status}</StatusPill>}
                  <span className="font-zilla text-lg font-semibold">{money(g.amount_cents)}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Side: tips + test tools */}
        <div className="space-y-4">
          <Card className="bg-ink text-cream" data-testid="donations-howto">
            <div className="text-[13px] uppercase tracking-widest font-bold text-gold mb-1">How it works</div>
            <p className="text-[14px] text-cream/85 leading-snug">Create a Goal campaign (goal + end date) or an ongoing Fund. Only <b>Live</b> funds are reachable publicly. Your admin totals always count every paid gift — the visibility switches only change what the public sees.</p>
          </Card>
          {isSuper && (
            <Card data-testid="donations-test-tools">
              <div className="text-[13px] font-semibold mb-1">Test the flow</div>
              <p className="text-[12px] text-ink/55 mb-2">Add a clearly-marked simulated gift. It never counts as paid and is cleared by Delete All Data.</p>
              <Button variant="secondary" data-testid="make-test-gift" onClick={makeTestGift} className="w-full"><Icon name="Award" className="w-4 h-4" /> Create a test gift</Button>
            </Card>
          )}
        </div>
      </div>

      <CampaignDrawer open={!!drawer} campaign={drawer} canEdit={canEdit}
        onClose={() => setDrawer(null)}
        onSaved={(saved, isNew) => { setDrawer(null); if (isNew && saved?.campaign_id) setView(saved.campaign_id); else load(); }}
        onDeleted={() => { setDrawer(null); load(); }} />
      <OfflineGiftModal open={offline} campaigns={campaigns} onClose={() => setOffline(false)} onSaved={() => { setOffline(false); load(); }} />
    </div>
  );
}
